import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedLayout from "@/components/layout/ProtectedLayout";
import LoginPage from "@/pages/LoginPage";
import BrandedLoginPage from "@/pages/BrandedLoginPage";
import TasksPage from "@/pages/tasks/TasksPage";
import TaskDetailPage from "@/pages/tasks/TaskDetailPage";
import ProfilePage from "@/pages/ProfilePage";
import CompanyUsersPage from "@/pages/admin/CompanyUsersPage";
import UsersImportPage from "@/pages/company/UsersImportPage";
import AllUsersPage from "@/pages/admin/AllUsersPage";
import ApprovalsPage from "@/pages/approvals/ApprovalsPage";
import ApprovalFormPage from "@/pages/approvals/ApprovalFormPage";
import ApprovalDetailPage from "@/pages/approvals/ApprovalDetailPage";
import DashboardPage from "@/pages/DashboardPage";
import DocumentsPage from "@/pages/DocumentsPage";
import NotificationsPage from "@/pages/NotificationsPage";
import WorkPage from "@/pages/WorkPage";
import ProposalViewPage from "@/pages/ProposalViewPage";
import ProposalsPage from "@/pages/admin/ProposalsPage";
import ProposalBuilderPage from "@/pages/admin/ProposalBuilderPage";
import SettingsPage from "@/pages/SettingsPage";
import ReportsPage from "@/pages/ReportsPage";
import ErpUploadsPage from "@/pages/ErpUploadsPage";
import UploadHistoryPage from "@/pages/UploadHistoryPage";
import ExpenseMappingPage from "@/pages/ExpenseMappingPage";
import SalesAnalysisPage from "@/pages/SalesAnalysisPage";
import LinePage from "@/pages/sections/LinePage";
import { CostOfSalesPage, ExpensesPage, OutstandingPage } from "@/pages/sections/SectionPages";
import CompaniesPage from "@/pages/admin/CompaniesPage";
import CompanyDetailPage from "@/pages/admin/CompanyDetailPage";
import VisibilityPage from "@/pages/admin/VisibilityPage";
import DataManagementPage from "@/pages/admin/DataManagementPage";
import RecordsPage from "@/pages/admin/RecordsPage";
import { Splash } from "@/components/Splash";
import AppIconPage from "@/pages/admin/AppIconPage";
import BrandingPage from "@/pages/company/BrandingPage";
import ViewerLinksPage from "@/pages/company/ViewerLinksPage";
import ShareLayout, { LegacyView, TabGate } from "@/pages/share/ShareLayout";

const LineRoute = () => { const { key } = useParams(); return <LinePage key={key} lineKey={key} />; };
const gate = (tab, el) => <TabGate tab={tab}>{el}</TabGate>;

function App() {
  return (
    <BrowserRouter>
      <Splash />
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/login/:slug" element={<BrandedLoginPage />} />
          <Route element={<ProtectedLayout />}>
            <Route path="/work" element={<WorkPage />} />
            <Route path="/tasks" element={<TasksPage />} />
            <Route path="/tasks/:id" element={<TaskDetailPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/approvals" element={<ApprovalsPage />} />
            <Route path="/approvals/new" element={<ApprovalFormPage />} />
            <Route path="/approvals/:id" element={<ApprovalDetailPage />} />
            <Route path="/approvals/:id/edit" element={<ApprovalFormPage />} />
            <Route path="/admin/companies/:id/users" element={<CompanyUsersPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/sales" element={<SalesAnalysisPage />} />
            <Route path="/outstanding" element={<OutstandingPage />} />
            <Route path="/expenses" element={<ExpensesPage />} />
            <Route path="/cogs" element={<CostOfSalesPage />} />
            <Route path="/lines/:key" element={<LineRoute />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/data/uploads" element={<ErpUploadsPage />} />
            <Route path="/data/history" element={<UploadHistoryPage />} />
            <Route path="/data/mapping" element={<ExpenseMappingPage />} />
            <Route path="/admin/companies" element={<CompaniesPage />} />
            <Route path="/admin/companies/:id" element={<CompanyDetailPage />} />
            <Route path="/admin/visibility" element={<VisibilityPage />} />
            <Route path="/admin/data" element={<DataManagementPage />} />
            <Route path="/admin/records" element={<RecordsPage />} />
            <Route path="/admin/app-icon" element={<AppIconPage />} />
            <Route path="/admin/users" element={<AllUsersPage />} />
            <Route path="/admin/proposals" element={<ProposalsPage />} />
            <Route path="/admin/proposals/:id" element={<ProposalBuilderPage />} />
            <Route path="/documents" element={<DocumentsPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/company/branding" element={<BrandingPage />} />
            <Route path="/company/sharing" element={<ViewerLinksPage />} />
            <Route path="/company/users-import" element={<UsersImportPage />} />
          </Route>
          <Route path="/documents/:id" element={<ProposalViewPage />} />
          <Route path="/view/:slug/*" element={<LegacyView />} />
          <Route path="/share/:lid" element={<ShareLayout />}>
            <Route path="dashboard" element={gate("dashboard", <DashboardPage />)} />
            <Route path="sales" element={gate("sales", <SalesAnalysisPage />)} />
            <Route path="outstanding" element={gate("outstanding", <OutstandingPage />)} />
            <Route path="expenses" element={gate("expenses", <ExpensesPage />)} />
            <Route path="cogs" element={gate("cogs", <CostOfSalesPage />)} />
            <Route path="reports" element={gate("reports", <ReportsPage />)} />
            <Route path="*" element={gate("none", null)} />
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
